import { object, string, ref, boolean, array, number } from "yup";
import isEmpty from "lodash/isEmpty";

// const phoneRegExp = /^(?:\+?61|0)[2-478](?:[ -]?[0-9]){8}$/;

const nameRegExp = /[^0-9]+$/;

// Blocks purely-numeric names but allows digits anywhere, unlike `nameRegExp`
// which rejects a trailing digit — e.g. "Grade 2".
const nonNumericNameRegExp = /[^0-9]/;
const providerNumberRegExp = /^[\w]+$/;
const suburbRegExp = /^[a-zA-Z ]+$/;
const stateRegExp = /^[a-zA-Z ]+$/;
const postCodeRegExp = /^[0-9]+$/;

// Validation schema for the login form
const loginSchema = object().shape({
  email: string().email("Email is invalid").required("Please enter email"),
  password: string().required("Please enter password"),
});

// Validation schema for the otp form
const otpSchema = object().shape({
  email: string().email("Email is invalid").required("Please enter email"),
  otp: string()
    .required("Please enter otp")
    .min(6, "Otp should be min 6 digit")
    .matches(/^[0-9-]+$/, "Please enter valid otp"),
});

// Validation schema for the forgot password form
const forgotPasswordSchema = object().shape({
  email: string().email("Email is invalid").required("Please enter email"),
});

// Validation schema for the reset password form
const resetPasswordSchema = object().shape({
  password: string().required("Please enter password"),
  confirmPassword: string()
    .required("Please enter confirm password")
    .oneOf([ref("password")], "Password must match"),
});

// Validation schema for the change password form
const changePasswordSchema = object().shape({
  oldPassword: string().required("Please enter old password"),
  password: string().required("Please enter password"),
  confirmPassword: string()
    .required("Please enter confirm password")
    .oneOf([ref("password")], "Password must match"),
});

// Validation schema for the add/edit health organisation form
const healthOrganisationSchema = object().shape(
  {
    name: string()
      .trim()
      .required("Hospital name is required")
      .max(100, "Hospital name cannot be longer than 100 characters")
      .matches(nameRegExp, "Invalid hospital name"),
    provider_number: string()
      .required("Provider number is required")
      .matches(providerNumberRegExp, "Invalid provider number")
      .test(
        "len",
        "Provider number should be of 8 characters",
        (val) => val.toString().length === 8
      ),
    unit_number: string()
      .trim()
      .max(100, "Unit Number cannot be longer than 100 characters"),
    street_number: string()
      .trim()
      .max(100, "Street Number cannot be longer than 100 characters"),
    street_name: string()
      .trim()
      .required("Street name is required")
      .max(100, "Street name cannot be longer than 100 characters"),
    suburb: string()
      .trim()
      .required("Suburb is required")
      .max(50, "Suburb cannot be longer than 50 characters")
      .matches(suburbRegExp, "Invalid suburb"),
    state: string()
      .trim()
      .required("State is required")
      .max(50, "State cannot be longer than 50 characters")
      .matches(stateRegExp, "Invalid suburb"),
    postcode: string()
      .required("Postcode is required")
      .max(4, "Postcode cannot be longer than 4 characters")
      .matches(postCodeRegExp, "Invalid postcode"),
    account_manager_mail: string()
      .email("Email is invalid")
      .required("Email is required"),
    phone: string().when("phone", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Phone number must be at least 10 characters")
          .max(10, "Phone number cannot be longer than 10 characters"),
    }),
    fax: string().when("fax", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Fax number must be at least 10 characters")
          .max(10, "Fax number cannot be longer than 10 characters"),
    }),
    mail: string().notRequired().email("Email is invalid"),
    is_fax_delivery: boolean().notRequired().nullable(),
    is_mail_delivery: boolean().notRequired().nullable(),
  },
  [
    // Add Cyclic deps here when require itself
    ["fax", "fax"],
    ["phone", "phone"],
  ]
);

// Validation schema for the update status form
const updateStatusSchema = object().shape({
  deactivate_reason: string().required("Please enter reason"),
});

// Validation schema for the add/edit laboratory form
const laboratorySchema = object().shape(
  {
    name: string()
      .trim()
      .required("Laboratory name is required")
      .max(100, "Laboratory name cannot be longer than 100 characters")
      .matches(nameRegExp, "Invalid laboratory name"),
    unit_number: string()
      .trim()
      .max(100, "Unit Number cannot be longer than 100 characters"),
    street_number: string()
      .trim()
      .max(100, "Street Number cannot be longer than 100 characters"),
    street_name: string()
      .trim()
      .required("Street name is required")
      .max(100, "Street name cannot be longer than 100 characters"),
    suburb: string()
      .trim()
      .required("Suburb is required")
      .max(50, "Suburb cannot be longer than 50 characters")
      .matches(suburbRegExp, "Invalid suburb"),
    state: string()
      .trim()
      .required("State is required")
      .max(50, "State cannot be longer than 50 characters")
      .matches(stateRegExp, "Invalid suburb"),
    account_manager_mail: string()
      .email("Email is invalid")
      .required("Email is required"),
    phone: string().when("phone", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Phone number must be at least 10 characters")
          .max(10, "Phone number cannot be longer than 10 characters"),
    }),
    postcode: string()
      .required("Postcode is required")
      .max(4, "Postcode cannot be longer than 4 characters")
      .matches(postCodeRegExp, "Invalid postcode"),
    fax: string().when("fax", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Fax number must be at least 10 characters")
          .max(10, "Fax number cannot be longer than 10 characters"),
    }),
    mail: string().when("mail", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () => string().email("Email is invalid"),
    }),
    is_fax_delivery: boolean().notRequired().nullable(),
    is_mail_delivery: boolean().notRequired().nullable(),
  },
  [
    // Add Cyclic deps here when require itself
    ["fax", "fax"],
    ["phone", "phone"],
    ["mail", "mail"],
  ]
);

// Validation schema for the add/edit user form
const userSchema = object().shape(
  {
    given_name: string().trim().required("First Name is required"),
    surname: string().trim().required("Last Name is required"),
    prefix: string().trim(),
    mail: string().email("Email is invalid").required("Email is required"),
    user_role_id: number()
      .typeError("User role is required")
      .required("User role is required"),
    is_super_admin: boolean().notRequired().nullable(),
    phone: string().when("is_super_admin", {
      is: true,
      then: (schema) =>
        schema
          .required("Phone number is required")
          .min(10, "Phone number must be at least 10 characters")
          .max(10, "Phone number cannot be longer than 10 characters"),
      otherwise: (schema) =>
        schema.when("phone", {
          is: (val: string | string[]) => val === "" || val === undefined,
          then: () => string().notRequired().nullable(),
          otherwise: () =>
            string()
              .min(10, "Phone number must be at least 10 characters")
              .max(10, "Phone number cannot be longer than 10 characters"),
        }),
    }),
    institution_id: string()
      .trim()
      .when("is_super_admin", {
        is: true,
        then: (schema) => schema.notRequired().nullable(),
        otherwise: (schema) => schema.required("Institution is required"),
      }),
  },
  [
    // Add Cyclic deps here when require itself
    ["phone", "phone"],
    ["mail", "mail"],
  ]
);

// Validation schema for the add/edit user role form
const userRoleSchema = object().shape({
  role_name: string().trim().required("Role Name is required"),
  endpoint_names: array().notRequired().nullable(),
});

const newUserSchema = object().shape(
  {
    prefix: string(),
    given_name: string().trim().required("First name is required"),
    surname: string().trim().required("Last name is required"),
    phone: string().when("phone", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Phone number must be at least 10 characters")
          .max(10, "Phone number cannot be longer than 10 characters"),
    }),
    mail: string().email("Email is invalid").required("Email is required"),
  },
  [
    // Add Cyclic deps here when require itself
    ["phone", "phone"],
  ]
);

const adminSchema = object().shape({
  prefix: string().trim().required("Prefix is required"),
  first_name: string().trim().required("First Name is required"),
  last_name: string().trim().required("Last Name is required"),
  title: string().trim().required("Title is required"),
  email: string().email("Email is invalid").required("Email is required"),
  user_role: string().trim().required("User role is required"),
  contact_number: string()
    .required("Phone number is required")
    .min(10, "Phone number must be at least 10 characters")
    .max(10, "Phone number cannot be longer than 10 characters"),
  password: string()
    .required("Please enter password")
    .min(12, "Password must be at least 12 characters"),
  create_password: boolean().notRequired().nullable(),
  change_password: boolean().notRequired().nullable(),
  email_info: boolean().notRequired().nullable(),
  password_change_required_for_first_sign_in: boolean()
    .notRequired()
    .nullable(),
  email_user_creds: boolean().notRequired().nullable(),
  create_automatic_password: boolean().notRequired().nullable(),
  hospital_id: string().notRequired().nullable(),
  laboratory_id: string().notRequired().nullable(),
});

// Validation schema for the add/edit surgeon form
const surgeonSchema = object().shape(
  {
    prefix: string().trim().required("Prefix is required"),
    speciality: string().trim().required("Speciality is required"),
    first_name: string()
      .trim()
      .required("First name is required")
      .matches(nameRegExp, "Invalid first name"),
    last_name: string()
      .trim()
      .required("Last name is required")
      .matches(nameRegExp, "Invalid last name"),
    street_address: string()
      .trim()
      .required("Street address is required")
      .max(100, "Street address cannot be longer than 100 characters"),
    additional_address_detail: string().trim(),
    suburb: string()
      .trim()
      .required("Suburb is required")
      .max(50, "Suburb cannot be longer than 50 characters")
      .matches(suburbRegExp, "Invalid suburb"),
    state: string()
      .trim()
      .required("State is required")
      .max(50, "State cannot be longer than 50 characters")
      .matches(stateRegExp, "Invalid suburb"),
    postcode: string()
      .required("Postcode is required")
      .max(4, "Postcode cannot be longer than 4 characters")
      .matches(postCodeRegExp, "Invalid postcode"),
    email: string().email("Email is invalid"),
    telephone: string().when("telephone", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Telephone number must be at least 10 characters")
          .max(10, "Telephone number cannot be longer than 10 characters"),
    }),
    fax_number: string().when("fax_number", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Fax number must be at least 10 characters")
          .max(10, "Fax number cannot be longer than 10 characters"),
    }),
    health_link: string().when("health_link", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(5, "Health link must be at least 5 characters")
          .max(20, "Health link cannot be longer than 20 characters"),
    }),
    provider_number: string().when("provider_number", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .matches(providerNumberRegExp, "Invalid provider number")
          .test(
            "len",
            "Provider number should be of 8 characters",
            (val) => val?.toString().length === 8
          ),
    }),
  },
  [
    // Add Cyclic deps here when require itself
    ["telephone", "telephone"],
    ["fax_number", "fax_number"],
    ["health_link", "health_link"],
    ["provider_number", "provider_number"],
  ]
);

// Validation schema for the select surgeon schema
const selectSurgeonSchema = object().shape({
  surgeon: number(),
});

// Validation schema for add new operating theatre form
const operatingTheatreSchema = object().shape({
  name: string().required("Theatre name is required"),
  room_no: string().trim(),
  floor: string().trim(),
  building: string().trim(),
});

const hospitalAdminSchema = object().shape({
  prefix: string(),
  title: string(),
  first_name: string().trim().required("First name is required"),
  last_name: string().trim().required("Last name is required"),
  contact_number: string()
    .required("Phone number is required")
    .min(10, "Phone number must be at least 10 characters")
    .max(10, "Phone number cannot be longer than 10 characters"),
  email: string().email("Email is invalid").required("Please enter email"),
});

const addressSchema = object().shape(
  {
    unit_number: string().trim(),
    street_number: string().trim(),
    street_name: string().trim()
    .required("Street name is required"),
    suburb: string()
      .trim()
      .required("Suburb is required")
      .max(50, "Suburb cannot be longer than 50 characters")
      .matches(suburbRegExp, "Invalid suburb"),
    state: string()
      .trim()
      .required("State is required")
      .max(50, "State cannot be longer than 50 characters")
      .matches(stateRegExp, "Invalid suburb"),
    postcode: string()
      .required("Postcode is required")
      .max(4, "Postcode cannot be longer than 4 characters")
      .matches(postCodeRegExp, "Invalid postcode"),
    mail: string().email("Email is invalid"),
    phone: string().when("phone", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Telephone number must be at least 10 characters")
          .max(10, "Telephone number cannot be longer than 10 characters"),
    }),
    fax: string().when("fax", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Fax number must be at least 10 characters")
          .max(10, "Fax number cannot be longer than 10 characters"),
    }),
    provider_number: string().when("provider_number", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .matches(providerNumberRegExp, "Invalid provider number")
          .test(
            "len",
            "Provider number should be of 8 characters",
            (val) => val?.toString().length === 8
          ),
    }),
  },
  [
    // Add Cyclic deps here when require itself
    ["phone", "phone"],
    ["fax", "fax"],
    ["provider_number", "provider_number"],
  ]
);

// Validation schema for the add/edit speciality form
const specialitySchema = object().shape({
  name: string()
    .trim()
    .required("Speciality name is required")
    .max(100, "Speciality name cannot be longer than 100 characters")
    .matches(nameRegExp, "Invalid speciality name"),
  clinical_diagram_ids: array().notRequired().nullable(),
});

// Validation schema for the add/edit test type form
const testTypeSchema = object().shape({
  name: string()
    .trim()
    .required("Test Type name is required")
    .max(100, "Test Type name cannot be longer than 100 characters")
    .matches(nameRegExp, "Invalid test type name"),
  speciality_ids: array()
    .min(1, "Specialities are required") // ensures at least one is selected
    .required("Specialities are required"),
  specimen_storage: string().trim().required("Specimen storage is required"),
  info: string().trim().notRequired(),
  is_intra_operative: boolean().notRequired().nullable(),
});

// Validation schema for the add/edit procedure form
const procedureSchema = object().shape({
  name: string()
    .trim()
    .required("Procedure name is required")
    .max(100, "Procedure name cannot be longer than 100 characters")
    .matches(nameRegExp, "Invalid procedure name"),
  speciality_ids: array()
    .min(1, "Specialities are required") // ensures at least one is selected
    .required("Specialities are required"),
});

// Reference data managed from the Super Admin module. Name rules mirror
// procedureSchema; site additionally requires the organ it belongs to.
const organSchema = object().shape({
  name: string()
    .trim()
    .required("Organ name is required")
    .max(100, "Organ name cannot be longer than 100 characters")
    .matches(nameRegExp, "Invalid organ name"),
});

const siteSchema = object().shape({
  name: string()
    .trim()
    .required("Site name is required")
    .max(100, "Site name cannot be longer than 100 characters"),
  organ_id: number()
    .typeError("Organ is required")
    .required("Organ is required"),
});

const lesionSchema = object().shape({
  name: string()
    .trim()
    .required("Lesion name is required")
    .max(100, "Lesion name cannot be longer than 100 characters")
    .matches(nonNumericNameRegExp, "Invalid lesion name"),
});

const metadataSchema = object().shape({
  name: string()
    .trim()
    .required("Metadata name is required")
    .max(100, "Metadata name cannot be longer than 100 characters")
    .matches(nonNumericNameRegExp, "Invalid metadata name"),
});

// Validation schema for the add/edit clinical diagram form
const clinicalDiagramSchema = object().shape({
  name: string()
    .trim()
    .required("Clinical diagram name is required")
    .max(100, "Clinical diagram name cannot be longer than 100 characters")
    .matches(nameRegExp, "Invalid clinical diagram name"),
});

// Export validation schemas for use in other parts of the application
/**
 * Schema for the add/associate surgeon flow.
 *
 * Notes:
 *   - Name/prefix/speciality are required only when no existing surgeon is
 *     selected (`$selectedSurgeonDetails`).
 *   - Address fields are required only when `$shouldRequireAddressInAddFlow`
 *     is set (non-super-admin, or a super-admin scoped to an org).
 */
const addSurgeonSchema = object().shape(
  {
    prefix: string().when("$selectedSurgeonDetails", {
      is: (v: unknown) => v && !isEmpty(v),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Prefix is required"),
    }),
    given_name: string().when("$selectedSurgeonDetails", {
      is: (v: unknown) => v && !isEmpty(v),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("First Name is required"),
    }),
    surname: string().when("$selectedSurgeonDetails", {
      is: (v: unknown) => v && !isEmpty(v),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Last Name is required"),
    }),
    speciality_id: number().when("$selectedSurgeonDetails", {
      is: (v: unknown) => v && !isEmpty(v),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Speciality is required"),
    }),
    unit_number: string().notRequired(),
    street_number: string().notRequired(),
    street_name: string().when(["$shouldRequireAddressInAddFlow"], {
      is: (v: unknown) => !!v,
      then: (schema) => schema.required("Street Name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    suburb: string().when("$shouldRequireAddressInAddFlow", {
      is: (v: unknown) => !!v,
      then: (schema) => schema.required("Suburb is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    state: string().when(["$shouldRequireAddressInAddFlow"], {
      is: (v: unknown) => !!v,
      then: (schema) => schema.required("State is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    postcode: string().when(["$shouldRequireAddressInAddFlow"], {
      is: (v: unknown) => !!v,
      then: (schema) => schema.required("Postcode is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    latitude: number(),
    longitude: number(),
    phone: string().when("phone", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Telephone number must be at least 10 characters")
          .max(10, "Telephone number cannot be longer than 10 characters"),
    }),
    fax: string().when("fax", {
      is: (val: string | string[]) => val === "",
      then: () => string().notRequired().nullable(),
      otherwise: () =>
        string()
          .min(10, "Fax number must be at least 10 characters")
          .max(10, "Fax number cannot be longer than 10 characters"),
    }),
    provider_number: string()
      .nullable()
      .notRequired()
      .when("provider_number", {
        is: (val: string) => !!val,
        then: (schema) =>
          schema
            .matches(providerNumberRegExp, "Invalid provider number")
            .test(
              "len",
              "Provider number should be of 8 characters",
              (val) => (val?.toString().length ?? 0) === 8
            ),
      }),
    mail: string().email("Email is invalid"),
  },
  [
    ["prefix", "prefix"],
    ["given_name", "given_name"],
    ["surname", "surname"],
    ["speciality_id", "speciality_id"],
    ["street_name", "street_name"],
    ["state", "state"],
    ["suburb", "suburb"],
    ["postcode", "postcode"],
    ["phone", "phone"],
    ["fax", "fax"],
    ["mail", "mail"],
    ["provider_number", "provider_number"],
  ]
);

export {
  loginSchema,
  addSurgeonSchema,
  otpSchema,
  forgotPasswordSchema,
  resetPasswordSchema,
  healthOrganisationSchema,
  laboratorySchema,
  userSchema,
  userRoleSchema,
  newUserSchema,
  adminSchema,
  changePasswordSchema,
  surgeonSchema,
  operatingTheatreSchema,
  hospitalAdminSchema,
  addressSchema,
  selectSurgeonSchema,
  providerNumberRegExp,
  specialitySchema,
  updateStatusSchema,
  testTypeSchema,
  procedureSchema,
  organSchema,
  siteSchema,
  lesionSchema,
  metadataSchema,
  clinicalDiagramSchema,
};
